import { useEffect, useMemo, useRef, useState } from 'react'
import { appToast } from '../../../../components/AppToast'
import PasswordComplexityRules from '../../../../components/PasswordComplexityRules'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { getStoredTenantUsername } from '../../../../utils/authSession'
import { evaluatePasswordComplexity } from '../../../../utils/passwordPolicy'
import {
  changePasswordRequest,
  requestForgotPasswordOtp,
  verifyForgotPasswordOtp,
} from '../../../auth/password-recovery/api/passwordRecoveryApi'
import OtpInputGroup from '../../../auth/password-recovery/components/OtpInputGroup'

const OTP_LENGTH = 6
const OTP_TIMER_SECONDS = 119

export default function ChangePasswordModal({
  isOpen,
  onClose,
  email,
  userName,
  tenantUsername: explicitTenantUsername,
}) {
  const [step, setStep] = useState('INITIATE') // 'INITIATE' | 'OTP' | 'NEW_PASSWORD' | 'SUCCESS'
  const [digits, setDigits] = useState(Array.from({ length: OTP_LENGTH }, () => ''))
  const [timerSeconds, setTimerSeconds] = useState(OTP_TIMER_SECONDS)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [verifiedOtp, setVerifiedOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const inputRefs = useRef(Array.from({ length: OTP_LENGTH }))
  const closeTimerRef = useRef(null)

  // Resolve active tenant username
  const resolvedTenant = useMemo(() => {
    return (explicitTenantUsername || getStoredTenantUsername() || '').trim().toUpperCase()
  }, [explicitTenantUsername])

  const normalizedEmail = useMemo(() => {
    return String(email || '').trim().toLowerCase()
  }, [email])

  const otpCode = useMemo(() => digits.join(''), [digits])

  const complexity = useMemo(() => {
    return evaluatePasswordComplexity(newPassword)
  }, [newPassword])

  const isPasswordMatch = newPassword && confirmPassword && newPassword === confirmPassword
  const canSubmitPassword = complexity.isMet && isPasswordMatch && !isSubmitting

  // Reset state whenever modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setStep('INITIATE')
      setDigits(Array.from({ length: OTP_LENGTH }, () => ''))
      setTimerSeconds(OTP_TIMER_SECONDS)
      setIsSubmitting(false)
      setError('')
      setVerifiedOtp('')
      setNewPassword('')
      setConfirmPassword('')
      setShowNewPassword(false)
      setShowConfirmPassword(false)
    } else {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    }
  }, [isOpen])

  // Countdown timer for OTP resend
  useEffect(() => {
    if (step !== 'OTP' || timerSeconds <= 0) {
      return undefined
    }
    const interval = window.setInterval(() => {
      setTimerSeconds((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => window.clearInterval(interval)
  }, [step, timerSeconds])

  // Escape key listener to close modal
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose?.()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isSubmitting, onClose])

  const formattedTimer = useMemo(() => {
    const minutes = String(Math.floor(timerSeconds / 60)).padStart(2, '0')
    const seconds = String(timerSeconds % 60).padStart(2, '0')
    return `${minutes}:${seconds}`
  }, [timerSeconds])

  // ── Step 1: Send OTP ──────────────────────────────────────────
  async function handleSendOtp() {
    setError('')
    if (!normalizedEmail) {
      setError('User email address is missing.')
      return
    }
    if (!resolvedTenant) {
      setError('Active tenant context could not be determined.')
      return
    }

    setIsSubmitting(true)
    try {
      await requestForgotPasswordOtp({
        tenantUsername: resolvedTenant,
        email: normalizedEmail,
      })
      setStep('OTP')
      setTimerSeconds(OTP_TIMER_SECONDS)
      setDigits(Array.from({ length: OTP_LENGTH }, () => ''))
      setTimeout(() => inputRefs.current[0]?.focus(), 150)
    } catch (err) {
      setError(
        extractApiErrorMessage(
          err,
          'Unable to send verification code. Please verify the email address and try again.'
        )
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Step 2: Resend OTP ────────────────────────────────────────
  async function handleResendOtp() {
    if (timerSeconds > 0 || isSubmitting) return
    setError('')
    setIsSubmitting(true)
    try {
      await requestForgotPasswordOtp({
        tenantUsername: resolvedTenant,
        email: normalizedEmail,
      })
      setTimerSeconds(OTP_TIMER_SECONDS)
      setDigits(Array.from({ length: OTP_LENGTH }, () => ''))
      appToast.success('Verification code resent. Check your inbox.')
      setTimeout(() => inputRefs.current[0]?.focus(), 150)
    } catch (err) {
      setError(
        extractApiErrorMessage(
          err,
          'Unable to resend verification code right now. Please try again later.'
        )
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Step 2: OTP Input Controls ────────────────────────────────
  function handleDigitChange(index, rawValue) {
    const value = rawValue.replace(/\D/g, '')
    setDigits((prev) => {
      const next = [...prev]
      next[index] = value.slice(-1)
      return next
    })
    setError('')

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  function handleDigitKeyDown(index, event) {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  function handleDigitPaste(event) {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    event.preventDefault()
    const nextDigits = Array.from({ length: OTP_LENGTH }, (_, idx) => pasted[idx] || '')
    setDigits(nextDigits)
    setError('')
    const nextFocus = Math.min(pasted.length, OTP_LENGTH - 1)
    inputRefs.current[nextFocus]?.focus()
  }

  // ── Step 2: Verify OTP ────────────────────────────────────────
  async function handleVerifyOtp(event) {
    event?.preventDefault?.()
    setError('')

    if (otpCode.length !== OTP_LENGTH || /\D/.test(otpCode)) {
      setError('Please enter the complete 6-digit verification code.')
      return
    }

    setIsSubmitting(true)
    try {
      await verifyForgotPasswordOtp({
        tenantUsername: resolvedTenant,
        email: normalizedEmail,
        otp: otpCode,
      })
      setVerifiedOtp(otpCode)
      setStep('NEW_PASSWORD')
    } catch (err) {
      setError(
        extractApiErrorMessage(
          err,
          'Invalid or expired verification code. Please check your code or request a new one.'
        )
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Step 3: Change Password Submit ───────────────────────────
  async function handleChangePasswordSubmit(event) {
    event?.preventDefault?.()
    setError('')

    if (!complexity.isMet) {
      setError('Password does not satisfy all complexity requirements.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.')
      return
    }
    if (!verifiedOtp) {
      setError('Verification code session expired. Please start verification again.')
      setStep('OTP')
      return
    }

    setIsSubmitting(true)
    try {
      await changePasswordRequest({
        tenantUsername: resolvedTenant,
        email: normalizedEmail,
        newPassword,
        otp: verifiedOtp,
      })
      appToast.success('Password changed successfully!')
      setStep('SUCCESS')

      // Auto-close after 2.5 seconds
      closeTimerRef.current = setTimeout(() => {
        onClose?.()
      }, 2500)
    } catch (err) {
      setError(
        extractApiErrorMessage(
          err,
          'Unable to update password right now. Please try again.'
        )
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-4 backdrop-blur-[2px] transition-all"
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-password-title"
    >
      <div className="relative w-full max-w-lg rounded-2xl border border-outline-variant/30 bg-surface p-6 sm:p-7 shadow-2xl transition-all">
        {/* Close Button */}
        <button
          type="button"
          onClick={() => {
            if (!isSubmitting) onClose?.()
          }}
          disabled={isSubmitting}
          className="absolute right-4 top-4 rounded-full p-1.5 text-on-surface-variant hover:bg-surface-container-high transition-colors disabled:opacity-50"
          aria-label="Close modal"
        >
          <span className="material-symbols-outlined text-xl">close</span>
        </button>

        {/* Modal Header */}
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-fixed text-primary">
            <span className="material-symbols-outlined text-2xl">lock_reset</span>
          </div>
          <div>
            <h3 id="change-password-title" className="text-lg font-bold text-on-surface">
              Change Password
            </h3>
            <p className="text-xs text-on-surface-variant">
              {userName ? `${userName} (${normalizedEmail})` : normalizedEmail}
            </p>
          </div>
        </div>

        {/* Error Alert Banner */}
        {error ? (
          <div
            className="mb-4 flex items-start gap-2.5 rounded-lg bg-error/10 px-3.5 py-2.5 text-xs text-error"
            role="alert"
          >
            <span className="material-symbols-outlined text-base">error</span>
            <span className="leading-relaxed">{error}</span>
          </div>
        ) : null}

        {/* ── STEP 1: INITIATE ───────────────────────────────────────── */}
        {step === 'INITIATE' ? (
          <div className="space-y-4">
            <div className="rounded-xl bg-surface-container-low p-4 text-xs leading-relaxed text-on-surface-variant space-y-2">
              <p>
                To ensure secure access management, a <strong>6-digit verification code</strong> will be sent to the registered email address:
              </p>
              <p className="font-mono text-sm font-semibold text-primary">
                {normalizedEmail}
              </p>
              <p className="text-[11px] text-outline">
                The code is valid for 15 minutes. You will need it to authorize the new password.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="rounded-lg border border-outline-variant/40 px-4 py-2 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-high transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={isSubmitting}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-on-primary hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {isSubmitting ? (
                  <>
                    <span className="material-symbols-outlined animate-spin text-sm">progress_activity</span>
                    Sending Code...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-sm">send</span>
                    Send Verification Code
                  </>
                )}
              </button>
            </div>
          </div>
        ) : null}

        {/* ── STEP 2: OTP VERIFICATION ───────────────────────────────── */}
        {step === 'OTP' ? (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <p className="text-xs text-on-surface-variant">
              Enter the 6-digit verification code sent to{' '}
              <strong className="text-on-surface">{normalizedEmail}</strong>:
            </p>

            <div className="py-2">
              <OtpInputGroup
                digits={digits}
                onDigitChange={handleDigitChange}
                onDigitKeyDown={handleDigitKeyDown}
                onDigitPaste={handleDigitPaste}
                inputRefs={inputRefs}
                disabled={isSubmitting}
              />
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="text-on-surface-variant">
                {timerSeconds > 0 ? (
                  <>Expires in <span className="font-mono font-semibold text-primary">{formattedTimer}</span></>
                ) : (
                  <span className="text-error font-medium">Code expired</span>
                )}
              </span>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={timerSeconds > 0 || isSubmitting}
                className="font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:text-outline-variant disabled:no-underline"
              >
                Resend Code
              </button>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-outline-variant/15">
              <button
                type="button"
                onClick={() => setStep('INITIATE')}
                disabled={isSubmitting}
                className="text-xs font-semibold text-on-surface-variant hover:text-primary transition-colors"
              >
                &larr; Back
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="rounded-lg border border-outline-variant/40 px-3.5 py-2 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-high transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={otpCode.length !== OTP_LENGTH || isSubmitting}
                  className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-on-primary hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <span className="material-symbols-outlined animate-spin text-sm">progress_activity</span>
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </button>
              </div>
            </div>
          </form>
        ) : null}

        {/* ── STEP 3: SET NEW PASSWORD ───────────────────────────────── */}
        {step === 'NEW_PASSWORD' ? (
          <form onSubmit={handleChangePasswordSubmit} className="space-y-4">
            {/* New Password */}
            <div>
              <label className="mb-1 block text-xs font-semibold text-on-surface-variant">
                New Password <span className="text-error">*</span>
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value)
                    setError('')
                  }}
                  disabled={isSubmitting}
                  placeholder="Enter new password"
                  className="w-full rounded-lg border-0 bg-surface-container-low px-3.5 py-2.5 pr-10 text-xs text-on-surface outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  tabIndex={-1}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface p-1"
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  <span className="material-symbols-outlined text-base">
                    {showNewPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="mb-1 block text-xs font-semibold text-on-surface-variant">
                Confirm Password <span className="text-error">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    setError('')
                  }}
                  disabled={isSubmitting}
                  placeholder="Re-enter new password"
                  className="w-full rounded-lg border-0 bg-surface-container-low px-3.5 py-2.5 pr-10 text-xs text-on-surface outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  tabIndex={-1}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface p-1"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <span className="material-symbols-outlined text-base">
                    {showConfirmPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              {confirmPassword && !isPasswordMatch ? (
                <p className="mt-1 text-[11px] text-error">Passwords do not match</p>
              ) : null}
            </div>

            {/* Reusable Complexity Checklist */}
            <PasswordComplexityRules complexity={complexity} />

            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-outline-variant/15">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="rounded-lg border border-outline-variant/40 px-4 py-2 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-high transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmitPassword}
                className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-on-primary hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <span className="material-symbols-outlined animate-spin text-sm">progress_activity</span>
                    Updating Password...
                  </>
                ) : (
                  'Update Password'
                )}
              </button>
            </div>
          </form>
        ) : null}

        {/* ── STEP 4: SUCCESS ────────────────────────────────────────── */}
        {step === 'SUCCESS' ? (
          <div className="space-y-4 py-4 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <span className="material-symbols-outlined text-3xl">check_circle</span>
            </div>
            <div>
              <h4 className="text-base font-bold text-on-surface">Password Updated Successfully</h4>
              <p className="mt-1 text-xs text-on-surface-variant">
                The password for <strong className="text-on-surface">{normalizedEmail}</strong> has been securely changed.
              </p>
            </div>
            <div className="pt-2">
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-on-primary hover:bg-primary/90 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
